import React, { useMemo, useRef, useState } from 'react';
import { App, Button, Space, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { documentService } from '@/services/document.service';
import { documentKeys } from '@/hooks/useDocuments';

const MAX_FILES = 10;

type Props = {
  deliveryId: string;
  deliveryNumber: string;
  disabled?: boolean;
};

/**
 * Upload proof-of-delivery files from the Delivery Note detail page
 * (same category as My Deliveries completion flow).
 */
export default function ProofOfDeliveryUpload({ deliveryId, deliveryNumber, disabled }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const fileList = useMemo(() => files.slice(0, MAX_FILES), [files]);
  const busy = uploading || disabled;

  const upload = async () => {
    const toUpload = fileList.filter((f) => f.originFileObj);
    if (toUpload.length === 0) {
      message.warning('Select at least one photo or PDF');
      return;
    }
    try {
      setUploading(true);
      for (const uploadFile of toUpload) {
        const file = uploadFile.originFileObj!;
        await documentService.uploadDocument(file, {
          document_name: file.name,
          entity_type: 'DeliveryNote',
          entity_id: deliveryId,
          document_category: 'ProofOfDelivery',
          document_type: file.type?.startsWith('image/') ? 'Image' : 'Document',
          description: `Proof of delivery for ${deliveryNumber}`,
        });
      }
      setFiles([]);
      await queryClient.invalidateQueries({ queryKey: documentKeys.byEntity('DeliveryNote', deliveryId) });
      await queryClient.invalidateQueries({ queryKey: ['deliveries', deliveryId] });
      await queryClient.invalidateQueries({ queryKey: ['deliveries', 'my-assignments'] });
      window.dispatchEvent(new Event(`refetch-documents-DeliveryNote-${deliveryId}`));
      message.success(
        toUpload.length === 1 ? 'Proof of delivery uploaded' : `${toUpload.length} proof files uploaded`
      );
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Upload
        multiple
        fileList={fileList}
        beforeUpload={() => false}
        onChange={({ fileList: next }) => setFiles(next.slice(0, MAX_FILES))}
        accept="image/*,application/pdf"
        disabled={busy}
      >
        <Button icon={<UploadOutlined />} disabled={busy}>
          Select files
        </Button>
      </Upload>
      <Space wrap>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => {
            const picked = e.target.files ? Array.from(e.target.files) : [];
            if (picked.length) {
              const pickedAsUpload = picked.map(
                (f) =>
                  ({
                    uid: `${f.name}-${f.lastModified}`,
                    name: f.name,
                    status: 'done',
                    originFileObj: f,
                  }) as UploadFile
              );
              setFiles((prev) => [...prev, ...pickedAsUpload].slice(0, MAX_FILES));
            }
            e.currentTarget.value = '';
          }}
        />
        <Button icon={<CameraOutlined />} onClick={() => cameraInputRef.current?.click()} disabled={busy}>
          Take picture
        </Button>
        <Button type="primary" loading={uploading} disabled={busy || fileList.length === 0} onClick={upload}>
          Upload proof
        </Button>
      </Space>
      <Typography.Text type="secondary">Photos or PDF — same proof used when marking delivered.</Typography.Text>
    </Space>
  );
}
